import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bell, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import NotificationPanel from './NotificationPanel';
import useNotifications from '../../../core/hooks/useNotifications';
import { ADMIN, hasProfile } from '../../auth/accessPolicy';
import { useProfileStore } from '../../../core/store/useProfileStore';
import useMediaQuery from '../../../core/hooks/useMediaQuery';

/**
 * **O** sino do header — não existe um segundo (decisão 1 do design).
 *
 * Ele substituiu o sino de R16, que contava só cartões vencendo hoje e levava
 * direto a `/revisao`. Por um tempo o número foi a soma de três fontes — não
 * lidas + revisão + (para admin) fila de moderação —, mas `app-shell-pencil-fidelity`
 * deu à revisão uma superfície própria (a pill do Topbar, o botão do App Bar)
 * para não repetir o mesmo número em dois lugares: o badge agora é **não
 * lidas + (para admin) fila de moderação**, e `due` só alimenta o bloco
 * "Revisão" do painel. O clique abre o painel com os três blocos, de onde
 * `/revisao` continua a um clique de distância.
 *
 * Consequência que o design registra e que aparece aqui: **o tooltip não
 * nomeia mais "cartões vencendo"** — só não lidas e, para admin, a fila de
 * moderação.
 *
 * `isAdmin` é lido do `useProfileStore` (semeado do `localStorage`, decide na
 * primeira pintura) em vez de vir do hook: o hook devolve `moderation: null`
 * tanto para o aluno quanto para o admin cuja leitura falhou, e o bloco precisa
 * distinguir os dois casos. É leitura de store, não chamada a mais — e manter a
 * superfície do hook intacta é deliberado, ela é fixada pelos testes dele.
 *
 * **Mobile vira drawer, não balão.** Um balão de 360px (a largura fixa do
 * dropdown desktop) não cabe dentro de uma viewport de 390px sem estourar a
 * borda direita, e é justamente no mobile que o aluno lê notificação. Abaixo
 * de 767px (o mesmo corte de `QuestionForum`/`SharedTextPanel`/`Header`) o
 * painel vira um drawer de página quase inteira, em portal para
 * `document.body` — mesma receita do overlay do fórum.
 */

const cx = (...v: Array<string | false | null | undefined>) => v.filter(Boolean).join(' ');

export interface NotificationBellProps {
  /**
   * `false` desliga as três leituras (`/notifications`, `/review/stats`,
   * `/moderation/stats`) sem desmontar o sino.
   *
   * O Header passa `enabled={!hideNav}` **além** de só montar o sino quando
   * `!hideNav`: a montagem condicional é o que tira o sino da página
   * pré-assinatura, e o `enabled` é a rede embaixo dela — se um dia o sino
   * voltar a ser montado sem condição, as chamadas continuam caladas em vez de
   * rodarem a cada navegação numa tela onde o usuário nem está assinando.
   */
  enabled?: boolean;
  className?: string;
}

/**
 * "7 itens pedem sua atenção: 1 não lida · 6 itens na fila de moderação".
 *
 * Exportada porque é a frase que o critério do tooltip descreve, e uma função
 * pura é o que um teste consegue fixar sem abrir o painel.
 */
export function bellSummary(parts: {
  total: number;
  unread: number;
  moderationQueue: number | null;
}): string {
  if (parts.total === 0) return 'Nada pendente';

  const pieces: string[] = [];
  if (parts.unread > 0) {
    pieces.push(`${parts.unread} ${parts.unread === 1 ? 'não lida' : 'não lidas'}`);
  }
  if (parts.moderationQueue && parts.moderationQueue > 0) {
    pieces.push(
      `${parts.moderationQueue} ${
        parts.moderationQueue === 1 ? 'item' : 'itens'
      } na fila de moderação`,
    );
  }

  const cabecalho = `${parts.total} ${parts.total === 1 ? 'item pede' : 'itens pedem'} sua atenção`;
  return pieces.length ? `${cabecalho}: ${pieces.join(' · ')}` : cabecalho;
}

export default function NotificationBell({ enabled = true, className }: NotificationBellProps) {
  const navigate = useNavigate();
  const profile = useProfileStore((state) => state.profile);
  const isAdmin = hasProfile(profile, ADMIN);
  const isMobile = useMediaQuery('(max-width: 767px)');

  const { items, unread, due, moderation, total, markRead, markAllRead, loading, error } =
    useNotifications(enabled);

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const bellRef = useRef<HTMLButtonElement | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);

  const moderationQueue =
    isAdmin && moderation ? moderation.pendingComments + moderation.openReports : null;
  const summary = bellSummary({ total, unread, moderationQueue });

  // Fechar clicando fora e no Escape. Sem isso o painel sobrevive à navegação
  // por um dos blocos e fica pendurado sobre a página de destino.
  //
  // `sheetRef` entra na checagem porque, no mobile, o painel é um portal em
  // `document.body` — fora da subárvore de `rootRef` — então um clique dentro
  // dele contaria como "fora" e fecharia o drawer no meio do próprio toque.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (sheetRef.current?.contains(target)) return;
      // Um `mousedown` num alvo não focável (o backdrop) tem, como ação
      // padrão do navegador, jogar o foco para `<body>` — depois de já termos
      // devolvido o foco ao sino no cleanup do efeito de baixo. Sem o
      // `preventDefault` aqui, essa ação padrão roda por último e desfaz o
      // `.focus()`, deixando o foco em `<body>` em vez de no sino.
      event.preventDefault();
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // O drawer mobile cobre a viewport inteira; sem travar o body, o dedo que
  // rola a lista de notificações também rola a página por baixo (o iOS Safari
  // em particular vaza scroll para o elemento por trás de um `fixed`).
  useEffect(() => {
    if (!open || !isMobile) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open, isMobile]);

  // O foco volta ao sino depois de qualquer forma de fechar — Esc, backdrop,
  // botão de fechar, ou o próprio clique no sino. Como o efeito roda de novo a
  // cada mudança de `open`, a limpeza do efeito anterior é exatamente o
  // instante em que `open` acabou de virar `false`.
  useEffect(() => {
    if (!open) return;
    return () => {
      bellRef.current?.focus();
    };
  }, [open]);

  const go = useCallback(
    (to: string) => {
      setOpen(false);
      navigate(to);
    },
    [navigate],
  );

  const panel = (
    <NotificationPanel
      variant={isMobile ? 'sheet' : 'dropdown'}
      items={items}
      unread={unread}
      due={due}
      moderation={moderation}
      isAdmin={isAdmin}
      loading={loading}
      error={error}
      onMarkRead={markRead}
      onMarkAllRead={markAllRead}
      onNavigate={go}
    />
  );

  return (
    <div ref={rootRef} className={cx('relative', className)}>
      <button
        ref={bellRef}
        type="button"
        data-testid="notification-bell"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={summary}
        title={summary}
        onClick={() => setOpen((previous) => !previous)}
        className="relative flex items-center leading-none transition-opacity hover:opacity-75"
      >
        <Bell size={18} strokeWidth={1.75} className="text-ink-secondary" />
        {total > 0 && (
          <span
            data-testid="notification-badge"
            // Redundante com o `aria-label` do botão, que já descreve a soma por
            // extenso — daí o `aria-hidden`.
            aria-hidden
            className="absolute -right-2 -top-1.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold leading-none text-brand-contrast"
          >
            {total}
          </span>
        )}
      </button>

      {/* Degrau da escada (`CarrosselForm.tsx`, `QuestionActionBar.tsx`): 950,
          acima da barra fixa de ações (900) e abaixo dos overlays de página
          inteira do fórum e dos modais (1000/1100). Em z-50 (o valor antigo) o
          painel ficava por baixo da barra mobile e o toque nela cobria o
          painel. */}
      {open && !isMobile && (
        <div className="absolute right-0 top-[calc(100%+0.5rem)] z-[950]">{panel}</div>
      )}

      {open &&
        isMobile &&
        createPortal(
          <div
            data-testid="notification-backdrop"
            className="fixed inset-0 z-[950] bg-black/50"
            onClick={() => setOpen(false)}
          >
            <div
              ref={sheetRef}
              data-testid="notification-sheet"
              role="dialog"
              aria-modal="true"
              aria-label="Notificações e pendências"
              onClick={(event) => event.stopPropagation()}
              className={cx(
                'fixed inset-x-0 top-16 z-[950] flex flex-col overflow-hidden',
                'max-h-[calc(100dvh-4rem)] rounded-b-modal border-b border-edge bg-surface shadow-panel',
              )}
              style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
              <div className="flex shrink-0 items-center justify-between gap-2 border-b border-edge px-4 py-3">
                <h2 className="m-0 text-sm font-bold text-ink">Notificações e pendências</h2>
                <button
                  type="button"
                  data-testid="notification-close"
                  aria-label="Fechar notificações"
                  onClick={() => setOpen(false)}
                  className="shrink-0 rounded p-1 text-ink-secondary transition-colors hover:bg-surface-hover hover:text-ink"
                >
                  <X size={18} aria-hidden />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">{panel}</div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
